import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Star, Plus, MessageCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fadeUp, waLink } from "@/lib/site-utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ n, size = 16, onSet }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((i) => (
      <button
        key={i}
        type={onSet ? "button" : undefined}
        onClick={onSet ? () => onSet(i) : undefined}
        data-testid={onSet ? `rate-star-${i}` : undefined}
        className={onSet ? "cursor-pointer" : "cursor-default"}
        aria-label={`${i} star`}
      >
        <Star size={size} className={i <= n ? "fill-jcb text-jcb" : "text-neutral-600"} />
      </button>
    ))}
  </div>
);

export const Testimonials = () => {
  const [reviews, setReviews] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", location: "", service: "construction", rating: 5, message: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/reviews`);
      setReviews(data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async () => {
    if (!form.name || !form.message) {
      toast.error("कृपया नाम और रिव्यू लिखें / Name & review required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, form);
      toast.success("धन्यवाद! आपका रिव्यू जुड़ गया / Review added");
      setForm({ name: "", location: "", service: "construction", rating: 5, message: "" });
      setOpen(false);
      load();
    } catch (e) {
      toast.error("कुछ गड़बड़ी हुई / Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="reviews" data-testid="reviews-section" className="relative border-t border-white/10 py-24 md:py-36">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <span className="font-plex text-xs tracking-[0.25em] uppercase text-terra">ग्राहक रिव्यू / REVIEWS</span>
            <h2 className="mt-4 font-khand font-700 uppercase tracking-tighter leading-[0.9] text-5xl md:text-7xl text-[#f5f5f0]">
              ग्राहकों की राय
            </h2>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <a
              data-testid="whatsapp-review-btn"
              href={waLink("नमस्ते SS Group! मैं अपना रिव्यू देना चाहता/चाहती हूँ:\n\nनाम: \nसेवा (Construction/Travels): \nरेटिंग (1-5): ⭐\nअनुभव: ")}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 bg-[#25D366] text-black px-6 py-3 font-khand font-600 tracking-wide hover:bg-white transition-colors duration-200"
            >
              <MessageCircle size={18} /> WhatsApp पर रिव्यू भेजें
            </a>

            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <button data-testid="add-review-btn" className="inline-flex items-center gap-2 border border-white/25 text-white px-6 py-3 font-khand font-600 tracking-wide hover:border-jcb hover:text-jcb transition-colors duration-200">
                  <Plus size={18} /> रिव्यू जोड़ें / Add Review
                </button>
              </DialogTrigger>
              <DialogContent className="bg-stone border-white/10 text-[#f5f5f0]">
                <DialogHeader>
                <DialogTitle className="font-khand text-3xl uppercase tracking-tight">अपना रिव्यू दें</DialogTitle>
              </DialogHeader>
              <div className="space-y-5 pt-2">
                <input data-testid="review-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="नाम / Name" className="w-full bg-transparent border-b border-white/20 focus:border-jcb outline-none py-2 font-hind" />
                <input data-testid="review-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="शहर / City" className="w-full bg-transparent border-b border-white/20 focus:border-jcb outline-none py-2 font-hind" />
                <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                  <SelectTrigger data-testid="review-service" className="w-full bg-transparent border-0 border-b border-white/20 rounded-none px-0 font-hind focus:ring-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-stone border-white/10 text-[#f5f5f0]">
                    <SelectItem value="construction" className="focus:bg-jcb focus:text-black">Construction</SelectItem>
                    <SelectItem value="travels" className="focus:bg-jcb focus:text-black">Travels</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-4">
                  <span className="font-plex text-[10px] tracking-[0.2em] uppercase text-neutral-400">रेटिंग</span>
                  <Stars n={form.rating} size={22} onSet={(i) => setForm({ ...form, rating: i })} />
                </div>
                <textarea data-testid="review-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} placeholder="आपका अनुभव…" className="w-full bg-transparent border-b border-white/20 focus:border-jcb outline-none py-2 font-hind resize-none" />
              </div>
              <DialogFooter>
                <button data-testid="review-submit-btn" onClick={submit} disabled={submitting} className="w-full bg-jcb text-black py-3 font-khand font-600 text-lg tracking-wide hover:bg-white transition-colors duration-200 disabled:opacity-60">
                  {submitting ? "भेज रहे हैं…" : "रिव्यू भेजें / Submit"}
                </button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {reviews.map((r, i) => (
            <motion.div
              key={r.id}
              variants={fadeUp}
              custom={i % 3}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-40px" }}
              data-testid={`review-card-${i}`}
              className="group border border-white/10 bg-stone/40 p-7 hover:bg-[#f5f5f0] transition-colors duration-300"
            >
              <div className="flex items-center justify-between">
                <Stars n={r.rating} />
                <span className={`font-plex text-[10px] tracking-[0.2em] uppercase ${r.service === "construction" ? "text-jcb" : "text-terra"} group-hover:text-black transition-colors`}>
                  {r.service}
                </span>
              </div>
              <p className="mt-5 font-hind text-neutral-200 leading-relaxed group-hover:text-neutral-800 transition-colors">“{r.message}”</p>
              <div className="mt-6 pt-4 border-t border-white/10 group-hover:border-black/10">
                <p className="font-khand text-xl text-[#f5f5f0] group-hover:text-black transition-colors">{r.name}</p>
                {r.location ? <p className="font-hind text-sm text-neutral-500 group-hover:text-neutral-600">{r.location}</p> : null}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
